import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, QrCode, MessageSquare, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";

const FormBuilder = () => {
  const { formId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<any>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [formUrl, setFormUrl] = useState("");

  useEffect(() => {
    if (formId) {
      fetchForm();
    }
  }, [formId]);

  const fetchForm = async () => {
    try {
      const { data: formData, error: formError } = await supabase
        .from("forms")
        .select("*, tables(*)")
        .eq("id", formId)
        .single();

      if (formError) throw formError;
      setForm(formData);

      const { data: fieldsData, error: fieldsError } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", formData.table_id)
        .order("order_index");

      if (fieldsError) throw fieldsError;
      setFields(fieldsData || []);

      // Generate form URL and QR code
      const url = `${window.location.origin}/form/${formData.share_token}`;
      setFormUrl(url);
      const qrDataUrl = await QRCode.toDataURL(url);
      setQrCodeDataUrl(qrDataUrl);
    } catch (error: any) {
      toast.error("Failed to load form");
    } finally {
      setLoading(false);
    }
  };

  const updateForm = async (updates: any) => {
    try {
      const { error } = await supabase
        .from("forms")
        .update(updates)
        .eq("id", formId);

      if (error) throw error;
      toast.success("Form updated");
      fetchForm();
    } catch (error: any) {
      toast.error("Failed to update form");
    }
  };

  const copyFormUrl = () => {
    navigator.clipboard.writeText(formUrl);
    toast.success("Form URL copied!");
  };

  const generateWhatsAppMessage = () => {
    const message = `Hi! Please fill out this form: ${formUrl}`;
    return `https://wa.me/?text=${encodeURIComponent(message)}`;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>Form not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-card">
        <div className="container mx-auto px-4">
          <div className="flex items-center h-16 gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="font-bold text-xl">Form Builder</h1>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Form Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Form Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                  onBlur={() => updateForm({ name: form.name })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description || ""}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  onBlur={() => updateForm({ description: form.description })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(checked) =>
                    updateForm({ is_active: checked })
                  }
                />
                <Label>Form is active</Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                WhatsApp Integration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Form URL</Label>
                <div className="flex gap-2">
                  <Input value={formUrl} readOnly />
                  <Button onClick={copyFormUrl} variant="outline">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Share on WhatsApp</Label>
                <Button
                  onClick={() => window.open(generateWhatsAppMessage(), "_blank")}
                  className="w-full"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Share via WhatsApp
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="h-5 w-5" />
                QR Code
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {qrCodeDataUrl && (
                <div className="flex justify-center">
                  <img src={qrCodeDataUrl} alt="Form QR Code" className="w-64 h-64" />
                </div>
              )}
              <Button
                onClick={() => {
                  const link = document.createElement("a");
                  link.download = `${form.name}-qr-code.png`;
                  link.href = qrCodeDataUrl;
                  link.click();
                }}
                variant="outline"
                className="w-full"
              >
                Download QR Code
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Form Fields ({fields.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {fields.map((field) => (
                  <div
                    key={field.id}
                    className="p-3 border border-border rounded-lg flex justify-between items-center"
                  >
                    <div>
                      <p className="font-medium">{field.name}</p>
                      <p className="text-sm text-muted-foreground">{field.type}</p>
                    </div>
                    {field.is_required && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                        Required
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
